/**
 * Event Handler Adapters
 * Provides utilities to adapt between different event types in HeroUI and React
 */

import React from 'react'

// Define PressEvent type locally since @react-types/shared is not installed
export interface PressEvent {
  type: 'press' | 'pressstart' | 'pressend' | 'pressup'
  pointerType: 'mouse' | 'touch' | 'pen' | 'keyboard' | 'virtual'
  target: Element
  shiftKey?: boolean
  ctrlKey?: boolean
  metaKey?: boolean
  altKey?: boolean
  preventDefault?: () => void
  stopPropagation?: () => void
}

/**
 * Adapts a React MouseEvent handler to work with HeroUI PressEvent
 */
export function adaptMouseToPressHandler<T extends HTMLElement = HTMLElement>(
  handler?: (e: React.MouseEvent<T>) => void
): ((e: PressEvent) => void) | undefined {
  if (!handler) return undefined
  
  return (e: PressEvent) => {
    // Create a synthetic mouse event with minimal required properties
    const syntheticEvent = {
      preventDefault: () => e.preventDefault?.(),
      stopPropagation: () => e.stopPropagation?.(),
      currentTarget: e.target as T,
      target: e.target as T,
      nativeEvent: new MouseEvent('click'),
      type: 'click' as const,
      bubbles: true,
      cancelable: true,
      timeStamp: Date.now(),
      isTrusted: true,
      button: 0,
      buttons: 1,
      clientX: 0,
      clientY: 0,
      detail: 1,
      altKey: false,
      ctrlKey: false,
      metaKey: false,
      shiftKey: false,
      movementX: 0,
      movementY: 0,
      pageX: 0,
      pageY: 0,
      relatedTarget: null,
      screenX: 0,
      screenY: 0,
      getModifierState: () => false,
    } as unknown as React.MouseEvent<T>
    
    handler(syntheticEvent)
  }
}

/**
 * Adapts a PressEvent handler to work with React MouseEvent
 */
export function adaptPressToMouseHandler<T extends HTMLElement = HTMLElement>(
  handler?: (e: PressEvent) => void  
): ((e: React.MouseEvent<T>) => void) | undefined {
  if (!handler) return undefined
  
  return (e: React.MouseEvent<T>) => {
    // Create a press event from mouse event
    const pressEvent: PressEvent = {
      type: 'press',
      pointerType: 'mouse',
      target: e.currentTarget as HTMLElement,
      shiftKey: e.shiftKey,
      ctrlKey: e.ctrlKey,
      metaKey: e.metaKey,
      altKey: e.altKey,
      preventDefault: () => e.preventDefault(),
      stopPropagation: () => e.stopPropagation(),
    }
    
    handler(pressEvent)
  }
}

/**
 * Creates a unified event handler that works with both event types
 */
export function createUnifiedHandler<T extends HTMLElement = HTMLElement>(
  handler: (e: React.MouseEvent<T> | PressEvent) => void
): {
  onClick?: (e: React.MouseEvent<T>) => void
  onPress?: (e: PressEvent) => void
} {
  return {
    onClick: handler as (e: React.MouseEvent<T>) => void,
    onPress: handler as (e: PressEvent) => void,
  }
}

/**
 * Type guard to check if an event is a PressEvent
 */
export function isPressEvent(e: any): e is PressEvent {
  return e && e.type === 'press' && 'pointerType' in e
}

/**
 * Type guard to check if an event is a MouseEvent
 */
export function isMouseEvent(e: any): e is React.MouseEvent {
  return e && 'nativeEvent' in e && e.nativeEvent instanceof MouseEvent
}

/**
 * Helper to create a handler that accepts optional events
 */
export function createOptionalEventHandler<T extends HTMLElement = HTMLElement>(
  handler: (e?: React.MouseEvent<T>) => void
): (e?: React.MouseEvent<T> | PressEvent) => void {
  return (e?: React.MouseEvent<T> | PressEvent) => {
    if (!e) {
      handler(undefined)
    } else if (isPressEvent(e)) {
      // Convert PressEvent to optional MouseEvent
      const syntheticEvent = {
        preventDefault: () => e.preventDefault?.(),
        stopPropagation: () => e.stopPropagation?.(),
        currentTarget: e.target as T,
        target: e.target as T,
        nativeEvent: new MouseEvent('click'),
        type: 'click' as const,
        bubbles: true,
        cancelable: true,
        timeStamp: Date.now(),
        isTrusted: false,
        button: 0,
        buttons: 1,
        clientX: 0,
        clientY: 0,
        detail: 1,
        altKey: false,
        ctrlKey: false,
        metaKey: false,
        shiftKey: false,
        movementX: 0,
        movementY: 0,
        pageX: 0,
        pageY: 0,
        relatedTarget: null,
        screenX: 0,
        screenY: 0,
        getModifierState: () => false,
      } as unknown as React.MouseEvent<T>
      handler(syntheticEvent)
    } else {
      handler(e as React.MouseEvent<T>)
    }
  }
}

/**
 * Adapts a simple void function to work as both onClick and onPress handler
 */
export function adaptVoidHandler(handler: () => void): {
  onClick: () => void
  onPress: () => void
} {
  return {
    onClick: handler,
    onPress: handler
  }
}

/**
 * Smart adapter that detects the handler type and returns appropriate props
 */
export function adaptEventHandler<T extends HTMLElement = HTMLElement>(
  handler: (() => void) | ((e: React.MouseEvent<T>) => void) | ((e: PressEvent) => void) | undefined
): { onClick?: (e: React.MouseEvent<T>) => void; onPress?: (e: PressEvent) => void } {
  if (!handler) return {}
  
  // Check if it's a void handler (no parameters)
  if (handler.length === 0) {
    return adaptVoidHandler(handler as () => void)
  }
  
  // Otherwise adapt as event handler
  return createUnifiedHandler<T>(handler as any)
}

/**
 * Adapter specifically for HeroUI Button components that only accept onPress
 */
export function adaptButtonHandler<T extends HTMLElement = HTMLElement>(
  handler: (() => void) | ((e: React.MouseEvent<T>) => void) | ((e: PressEvent) => void) | undefined
): { onPress?: (e: PressEvent) => void } {
  if (!handler) return {}
  
  // Check if it's a void handler (no parameters)
  if (handler.length === 0) {
    return { onPress: () => (handler as () => void)() }
  }
  
  // Convert MouseEvent handler to PressEvent handler
  return {
    onPress: (e: PressEvent) => {
      const syntheticEvent = {
        preventDefault: () => e.preventDefault?.(),
        stopPropagation: () => e.stopPropagation?.(),
        currentTarget: e.target as T,
        target: e.target as T,
        nativeEvent: new MouseEvent('click'),
        type: 'click' as const,
        bubbles: true,
        cancelable: true,
        timeStamp: Date.now(),
        isTrusted: false,
        button: 0,
        buttons: 1,
        clientX: 0,
        clientY: 0,
        detail: 1,
        altKey: false,
        ctrlKey: false,
        metaKey: false,
        shiftKey: false,
        movementX: 0,
        movementY: 0,
        pageX: 0,
        pageY: 0,
        relatedTarget: null,
        screenX: 0,
        screenY: 0,
        getModifierState: () => false,
      } as unknown as React.MouseEvent<T>
      
      ;(handler as any)(syntheticEvent)
    }
  }
}